import {
  ANIMATION_VERSION,
  CELL,
  PIVOT,
  animationFrame,
  atlasClips,
} from "./animation";
import type { Animated, Vec2 } from "./types";
import { drawLiving, runtimeArtState } from "./living-art";
import { strideLength } from "./motion";
import { loadPaintedCores } from "./painted-cores";
import { loadInkAssets, inkRevision } from "./ink-assets";
import { RIGS, rigStride } from "./botanical-rig";

/** 发布资源采用版本化文件名；只读加载，失败时保留可玩的程序绘制回退。 */
export class GameArt {
  constructor(private readonly living = false) {
    if (living) {
      loadPaintedCores();
      loadInkAssets();
    }
  }
  readonly island = this.load("/art/island-v3.png");
  private animations = new Map<string, HTMLImageElement>();
  private objectFrames = new Map<string, HTMLCanvasElement>();
  private gaitPhases = new WeakMap<
    Animated,
    { distance: number; phase: number }
  >();
  animated(
    ctx: CanvasRenderingContext2D,
    id: string,
    actor: Animated & { velocity?: Vec2 },
    time: number,
    width: number,
    height = width,
    flip = false,
    environment = false,
  ): boolean {
    if (this.living) {
      const state = runtimeArtState(actor, time, environment);
      const speed = actor.velocity
        ? Math.hypot(actor.velocity.x, actor.velocity.y)
        : 0;
      if (
        !environment &&
        !actor.motion &&
        !actor.animation &&
        speed > 4 &&
        RIGS[id]
      )
        state.motion =
          speed > (id === "hero" ? 170 : RIGS[id].gait === "heavy" ? 180 : 190)
            ? "run"
            : "walk";
      if (
        !environment &&
        speed > 4 &&
        (!actor.motion || ["start", "stop"].includes(actor.motion.id)) &&
        !actor.animation
      ) {
        const v = actor.velocity!;
        state.facing =
          Math.abs(v.y) > Math.abs(v.x) * 1.1
            ? v.y < 0
              ? "north"
              : "south"
            : v.x < 0
              ? "west"
              : "east";
      }
      if (
        !environment &&
        speed > 4 &&
        !["death", "rest", "dash", "stagger"].includes(state.motion)
      ) {
        const distance = actor.stride ?? 0,
          previous = this.gaitPhases.get(actor);
        const gaitMotion =
          speed > (id === "hero" ? 170 : RIGS[id]?.gait === "heavy" ? 180 : 190)
            ? "run"
            : "walk";
        state.travel = {
          x: actor.velocity!.x / speed,
          y: actor.velocity!.y / speed,
        };
        const delta = previous ? Math.max(0, distance - previous.distance) : 0;
        const stride =
          ((RIGS[id]
            ? rigStride(id, gaitMotion, state.facing, state.travel)
            : strideLength(gaitMotion)) *
            width) /
          160;
        const phase = ((previous?.phase ?? 0) + delta / stride) % 1;
        state.gait = { motion: gaitMotion, phase };
        if (state.motion === "walk" || state.motion === "run")
          state.phase = phase;
        this.gaitPhases.set(actor, { distance, phase });
      } else if (!environment && state.motion !== "stop")
        this.gaitPhases.delete(actor);
      if (environment) {
        const step = Math.floor((time % 4) * 8),
          key = `${inkRevision}:${id}:${state.active !== false}:${state.motion}:${Math.round(state.phase * 16)}:${step}:${Math.round((state.health ?? 1) * 3)}`;
        let frame = this.objectFrames.get(key);
        if (!frame) {
          frame = document.createElement("canvas");
          frame.width = 160;
          frame.height = 160;
          const c = frame.getContext("2d")!;
          c.translate(80, 148);
          drawLiving(c, id, { ...state, time: step / 8 });
          this.objectFrames.set(key, frame);
          if (this.objectFrames.size > 256)
            this.objectFrames.delete(this.objectFrames.keys().next().value!);
        }
        ctx.drawImage(frame, -width / 2, (-height * 148) / 160, width, height);
      } else {
        ctx.save();
        ctx.scale(width / 160, height / 160);
        drawLiving(ctx, id, state);
        ctx.restore();
      }
      return true;
    }
    let image = this.animations.get(id);
    if (!image) {
      image = this.load(`/art/animation-v${ANIMATION_VERSION}/${id}.webp`);
      this.animations.set(id, image);
    }
    if (!this.ready(image)) return false;
    const state = animationFrame(actor, time, environment);
    const rows = atlasClips(id, environment);
    const row = rows.indexOf(state.clip);
    if (row < 0) return false;
    const atlasRow =
      row + (!environment && row > 1 ? 1 : 0) + Math.floor(state.frame / 8);
    ctx.save();
    if (flip) ctx.scale(-1, 1);
    ctx.drawImage(
      image,
      (state.frame % 8) * CELL,
      atlasRow * CELL,
      CELL,
      CELL,
      -width / 2,
      (-height * PIVOT.y) / CELL,
      width,
      height,
    );
    ctx.restore();
    return true;
  }
  private load(url: string): HTMLImageElement {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    return image;
  }
  ready(image: HTMLImageElement): boolean {
    return image.complete && image.naturalWidth > 0;
  }
  sprite(
    ctx: CanvasRenderingContext2D,
    sheet: HTMLImageElement,
    index: number,
    width: number,
    height: number,
    flip = false,
  ): boolean {
    if (!this.ready(sheet)) return false;
    const cw = sheet.naturalWidth / 3,
      ch = sheet.naturalHeight / 3;
    ctx.save();
    if (flip) ctx.scale(-1, 1);
    ctx.drawImage(
      sheet,
      (index % 3) * cw + 2,
      Math.floor(index / 3) * ch + 2,
      cw - 4,
      ch - 4,
      -width / 2,
      -height + 9,
      width,
      height,
    );
    ctx.restore();
    return true;
  }
}
